import Logger from "./Logger.ts";
import {
  getNow,
  isDefined,
  isFunction,
  isString,
  isTrackingEnabled
} from "./utils.ts";
import Controller, { DefaultController } from "./Controller.ts";
import { DEFAULT_CLIENT_CONFIG, SDK_VERSION } from "./constants.ts";
import EventLogger from "./AutoEventLogger.ts";
import type {
  ClientConfig,
  QueueEntry,
  SuccessCallback,
  ErrorCallback,
  RedirectionHandler
} from "./types/types.ts";

export default class KlariticsSDK {
  _isInitialized: boolean = false;
  _queue: QueueEntry[] = [];
  version: string | number = SDK_VERSION;
  _isFlutter: boolean = false;
  _flutterHelper: object | null = null;
  window: Window & typeof globalThis;
  _startTime: number;
  _controller!: InstanceType<typeof Controller> | typeof DefaultController;
  _q?: QueueEntry[] = [];

  constructor(
    window: Window & typeof globalThis,
    startTime: number = getNow(),
    queue: QueueEntry[] = []
  ) {
    this.window = window;
    this._startTime = startTime;
    this._q = queue;
    this._init();
  }

  _init = (): void => {
    if (Array.isArray(this._q)) {
      this._q.forEach(({ m, args }) => {
        const method = (this as Record<string, unknown>)[m];
        if (isDefined(method) && isFunction(method)) {
          (method as (...a: unknown[]) => void).apply(this, Array.from(args));
        }
      });
    }
  };

  /**
   * Initialise the SDK.
   * @param siteId        - Your site identifier.
   * @param host          - Your Host Name
   * @param clientConfig  - Optional configuration overrides.
   * @param successCallback - Called with `{ client_id, session_id }` on success.
   * @param errorCallback   - Called with an optional error value on failure.
   */
  init = (
    siteId: string,
    host: string,
    clientConfig: ClientConfig = DEFAULT_CLIENT_CONFIG,
    successCallback: SuccessCallback | null = null,
    errorCallback: ErrorCallback | null = null
  ): void => {
    try {
      Logger.debug("SDK : Klaritics SDK initialization has started");

      if (this._isInitialized) {
        Logger.warn("SDK : Already initialized. Skipping duplicate init call.");
        return;
      }

      if (!this._isInitialized) {
        if (!isDefined(clientConfig) || !(clientConfig instanceof Object)) {
          clientConfig = DEFAULT_CLIENT_CONFIG;
        }

        let {
          honorDNT = false,
          debug = false,
          events_upload_time = -1,
          retry_time = -1,
          level = "error",
          auto_event_logging = false
        } = clientConfig;

        const localDebug = this.window?.localStorage?.getItem(
          "klaritics_debug_enable"
        );
        if (localDebug !== null) {
          debug = localDebug === "true";
        }

        const localLevel = this.window?.localStorage?.getItem(
          "klaritics_log_level"
        );
        if (localLevel !== null) {
          level = localLevel;
        }

        this._configureLogger(debug, level);

        if (
          !isDefined(siteId) ||
          !isDefined((siteId as string).trim) ||
          (siteId as string).trim() === ""
        ) {
          console.error(
            `SDK : KlariticsSDK couldn't be initialized: Invalid Site Id:${siteId}`
          );
          this._controller = DefaultController;
          errorCallback?.();
          return;
        }

        if (
          !isString(host) ||
          !isDefined(host) ||
          !isDefined((host as string).trim) ||
          (host as string).trim() === ""
        ) {
          console.error(
            `SDK : KlariticsSDK couldn't be initialized: Invalid Host Name:${host}`
          );
          this._controller = DefaultController;
          errorCallback?.();
          return;
        }

        if (honorDNT && !isTrackingEnabled(this.window)) {
          this._controller = DefaultController;
          errorCallback?.();
          Logger.error(
            "SDK : Tracking is disabled, KlariticsSDK couldn't be initialized"
          );
          return;
        }

        this._controller = Controller.getInstance();

        if (
          isDefined(events_upload_time) &&
          (events_upload_time as number) > 0
        ) {
          clientConfig.events_upload_time =
            (events_upload_time as number) * 1000;
        }

        if (isDefined(retry_time) && (retry_time as number) > 0) {
          clientConfig.retry_time = (retry_time as number) * 1000;
        }

        this._controller.initialize(
          siteId,
          host,
          this._startTime,
          clientConfig,
          this.window,
          () => {
            // ── Success ──────────────────────────────────────────────────────
            this._isInitialized = true;

            this._queue.forEach(({ m, args }) => {
              const fn = (this._controller as Record<string, unknown>)[m];
              if (isFunction(fn)) {
                (fn as (...a: unknown[]) => void).apply(
                  this._controller,
                  Array.from(args)
                );
              }
            });

            if (successCallback !== null && isFunction(successCallback)) {
              Logger.debug("SDK : Device id is " + this.getClientId());
              successCallback({
                client_id: this.getClientId(),
                session_id: this.getSessionId()
              });
            }

            this.window.console.info(
              `KlariticsSDK initialized for site:${siteId} with SDK version: ${SDK_VERSION}`
            );

            if (auto_event_logging) {
              const autoEventLogging = new EventLogger();
              Logger.debug("auto_event_logging", autoEventLogging);
            }
          },
          () => {
            // ── Error ────────────────────────────────────────────────────────
            this._isInitialized = false;
            Logger.error("SDK : Failed to initialize Klaritics SDK");
            errorCallback?.("");
          }
        );
      }
    } catch (e: unknown) {
      (this._controller as { _isInitialized: boolean })._isInitialized = false;
      Logger.error("SDK : Exception while initializing Klaritics SDK : ", e);
      errorCallback?.(e);
    }
  };

  private _configureLogger = (
    debug: boolean = false,
    level: string = "error"
  ): void => {
    Logger.enabled = debug;
    Logger.level = level;
    Logger.console = this.window.console;
    Logger.debug(`SDK : Logger configured with level ${level}`);
  };

  // ── Flutter helpers ─────────────────────────────────────────────────────────

  /** Sets whether this instance is running inside a Flutter app. */
  setIsFlutter = (isFlutter: boolean): void => {
    this._isFlutter = isFlutter;
  };

  /** Returns `true` when running inside a Flutter app. */
  isFlutter = (): boolean => {
    return this._isFlutter;
  };

  /**
   * Registers the Dart helper object used for Flutter ↔ Web SDK communication.
   */
  registerKlariticsFlutterHelper = (obj: object): void => {
    this._flutterHelper = obj;
  };

  /** Returns the registered Flutter helper object. */
  getKlariticsFlutterHelper(): object | null {
    return this._flutterHelper;
  }

  // ── Event logging ───────────────────────────────────────────────────────────

  logEvent = (
    name: string,
    additionalInfo?: Record<string, unknown>,
    forceReport: boolean = false,
    loggedBy: string = "customer"
  ): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "logAppEvent", args: [name, additionalInfo] });
      return;
    }
    this._controller.logAppEvent(
      name,
      additionalInfo,
      undefined,
      forceReport,
      loggedBy
    );
  };

  logClientEvent = (
    name: string,
    additionalInfo?: Record<string, unknown>
  ): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "logClientEvent", args: [name, additionalInfo] });
      return;
    }
    this._controller.logClientEvent(name, additionalInfo);
  };

  dispatchValidationEvent = (
    name: string,
    additionalInfo: Record<string, unknown> = {}
  ): void => {
    if (!this._isInitialized) {
      this._queue.push({
        m: "dispatchValidationEvent",
        args: [name, additionalInfo]
      });
      return;
    }
    this._controller.dispatchValidationEvent(name, additionalInfo);
  };

  // ── Page view ───────────────────────────────────────────────────────────────

  /** Log a page view — useful for SPAs. */
  logPageView = (pathname: string): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "logPageView", args: [pathname] });
      return;
    }
    this._controller.logPageView(pathname);
  };

  // ── User / session properties ───────────────────────────────────────────────

  /** Set a custom user identifier. */
  setUserId = (userId: string): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "setUserId", args: [userId] });
      return;
    }
    this._controller.setUserId(userId);
  };

  /** Bulk-set user properties. */
  setUserProperties = (properties: Record<string, unknown>): void => {
    Logger.debug("SDK : User properties set");
    if (!this._isInitialized) {
      this._queue.push({ m: "setUserProperties", args: [properties] });
      return;
    }
    this._controller.setUserProperties(properties);
  };

  /** Reset all user properties. */
  resetUserProperties = (): void => {
    Logger.debug("SDK : User properties reset");
    if (!this._isInitialized) {
      this._queue.push({ m: "setUserProperties", args: [] });
      return;
    }
    this._controller.resetUserProperties();
  };

  /** Reset all session properties. */
  resetSessionProperties = (): void => {
    Logger.debug("SDK : User properties reset");
    if (!this._isInitialized) {
      this._queue.push({ m: "setUserProperties", args: [] });
      return;
    }
    this._controller.resetSessionProperties();
  };

  /** Clear localStorage keys unrelated to Klaritics. */
  clearLocalStorage = (): void => {
    Logger.debug("SDK : Clearing keys which are not related to antor");
    if (!this._isInitialized) {
      this._queue.push({ m: "clearLocalStorage", args: [] });
      return;
    }
    this._controller.clearLocalStorage();
  };

  /** Bulk-set session properties. */
  setSessionProperties = (properties: Record<string, unknown>): void => {
    Logger.debug("SDK : Session properties set");
    if (!this._isInitialized) {
      this._queue.push({ m: "setSessionProperties", args: [properties] });
      return;
    }
    this._controller.setSessionProperties(properties);
  };

  // ── App metadata ────────────────────────────────────────────────────────────

  /** Set the application version string. */
  setAppVersion = (version: string): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "setAppVersion", args: [version] });
      return;
    }
    this._controller.setAppVersion(version);
  };

  flattenJSON = (properties: Record<string, unknown>): void => {
    if (!this._isInitialized) {
      this._queue.push({ m: "flattenJSON", args: [properties] });
      return;
    }
    this._controller.flattenJSON(properties);
  };

  // ── Getters ─────────────────────────────────────────────────────────────────

  /** Returns the current session ID, or `null` if not initialised. */
  getSessionId = (): string | void | null => {
    if (!this._isInitialized) return null;
    return this._controller.getSessionId();
  };

  /** Returns the client / device ID. */
  getClientId = (): string | null | void => {
    return this._controller?.getDeviceId();
  };

  // ── Session lifecycle ───────────────────────────────────────────────────────

  startNewSession = (): void => {
    Logger.debug("SDK : A new session started");
    if (!this._isInitialized) {
      this._queue.push({ m: "startNewSession", args: [] });
      return;
    }
    this._controller.startNewSession(Date.now());
  };

  endSession = (): void => {
    Logger.debug("SDK : Current session ended");
    if (!this._isInitialized) {
      this._queue.push({ m: "endSession", args: [] });
      return;
    }
    this._controller.endSession();
  };

  // ── Miscellaneous ───────────────────────────────────────────────────────────

  // getController = (): Controller | typeof DefaultController => {
  //   return this._controller;
  // };

  getSiteId = (): string | null | void => {
    return this._controller?.getSiteId();
  };

  setRedirectionHandler = (callback: RedirectionHandler): void => {
    if (!isFunction(callback)) {
      Logger.error("Callback must be a function");
      return;
    }

    if (!this._isInitialized) {
      this._queue.push({ m: "setRedirectionHandler", args: [callback] });
      return;
    }

    this._controller.setRedirectionHandler(callback);
  };

  setInitFlag = (initialized: boolean): void => {
    this._isInitialized = initialized;
  };

  setKlariticsEventListener(AEL: unknown): void {
    this._controller.setKlariticsEventListener(AEL);
  }
}
